/** Large section sources live in a custom XML part, not short Word properties. */
export const REPORT_SOURCE_NAMESPACE = "urn:jenly:report-source:v1";
export const REPORT_RENDER_VERSION = 1;

/** Portable grounding has the same exclusions as an ordinary-user download context. */
export function exportSafeSource(value: any): any {
  if (Array.isArray(value)) return value.map(exportSafeSource);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !["tool_call", "tool_name", "read_url", "blob_path"].includes(key))
    .map(([key, item]) => [key, exportSafeSource(item)]));
  if (typeof value === "string" && /^https?:\/\//.test(value)) {
    try {
      const url = new URL(value);
      if (url.hostname.endsWith(".blob.core.windows.net") || url.searchParams.has("sig")) return null;
    } catch (_) { return null; }
  }
  return value;
}

export interface ReportSourcePayload {
  version: 1;
  revision: number;
  download_id: string;
  run_id: string;
  project_id: string;
  template_id: string;
  sections: any[];
}

export function parseReportSource(xml: string): ReportSourcePayload {
  const document = new DOMParser().parseFromString(xml, "application/xml");
  const root = document.documentElement;
  if (document.getElementsByTagName("parsererror").length || root.localName !== "report" ||
      root.namespaceURI !== REPORT_SOURCE_NAMESPACE) throw new Error("Invalid report source XML.");
  const nodes = root.getElementsByTagNameNS(REPORT_SOURCE_NAMESPACE, "payload");
  if (nodes.length !== 1) throw new Error("Invalid report source payload.");
  const payload = JSON.parse(nodes[0].textContent || "");
  validateReportSource(payload);
  return payload;
}

export function validateReportSource(payload: any, expected?: Partial<ReportSourcePayload>): asserts payload is ReportSourcePayload {
  if (payload?.version !== 1 || !Number.isInteger(payload.revision) || payload.revision < 0 ||
      !Array.isArray(payload.sections)) throw new Error("Unsupported report source payload.");
  for (const key of ["download_id", "run_id", "project_id", "template_id"]) {
    if (typeof payload[key] !== "string" || !payload[key] || (expected?.[key] && expected[key] !== payload[key])) {
      throw new Error("Report source identity does not match this Word document.");
    }
  }
  const ids = new Set();
  const keys = new Set();
  for (const section of payload.sections) {
    if (typeof section.settings_group_id !== "string" || !section.settings_group_id || typeof section.insertion_key !== "string" || !section.insertion_key ||
        typeof section.content !== "string" || ids.has(section.settings_group_id) || keys.has(section.insertion_key)) {
      throw new Error("Invalid or duplicate report source section.");
    }
    ids.add(section.settings_group_id);
    keys.add(section.insertion_key);
    if (section.grounding?.task_id && section.grounding.task_id !== section.agent_task_id) {
      throw new Error("Section grounding does not match its generation task.");
    }
  }
}

export function serializeReportSource(payload: ReportSourcePayload): string {
  validateReportSource(payload);
  const xml = document.implementation.createDocument(REPORT_SOURCE_NAMESPACE, "report", null);
  const node = xml.createElementNS(REPORT_SOURCE_NAMESPACE, "payload");
  node.textContent = JSON.stringify(payload);
  xml.documentElement.appendChild(node);
  return new XMLSerializer().serializeToString(xml);
}

async function readInContext(context: Word.RequestContext) {
  if (typeof Office !== "undefined" && Office.context?.requirements && !Office.context.requirements.isSetSupported("WordApi", "1.4")) {
    throw new Error("Report reparsing requires Word API 1.4. Update Word to restore this report's sections.");
  }
  const parts = context.document.customXmlParts.getByNamespace(REPORT_SOURCE_NAMESPACE);
  parts.load("items");
  await context.sync();
  if (parts.items.length > 1) throw new Error("Duplicate embedded report sources.");
  const part = parts.items[0];
  if (!part) return { part: null, payload: null };
  const xml = part.getXml();
  await context.sync();
  return { part, payload: parseReportSource(xml.value) };
}

export const readReportSource = (): Promise<ReportSourcePayload | null> =>
  Word.run(async (context) => (await readInContext(context)).payload);

let sourceQueue: Promise<unknown> = Promise.resolve();

/** Serialize read-modify-write operations so concurrent generation cannot lose sections. */
export function mutateReportSource(
  change: (payload: ReportSourcePayload | null) => ReportSourcePayload | null
): Promise<ReportSourcePayload | null> {
  const run = () => Word.run(async (context) => {
    const { part, payload } = await readInContext(context);
    const next = change(payload);
    if (!next) return payload;
    next.revision = (payload?.revision || 0) + 1;
    const xml = serializeReportSource(next);
    if (part) part.updateElement("/j:report", xml, { j: REPORT_SOURCE_NAMESPACE });
    else context.document.customXmlParts.add(xml);
    await context.sync();
    return next;
  });
  const result = sourceQueue.then(run, run);
  sourceQueue = result.catch(() => undefined);
  return result;
}

export function normalizeSectionText(text: string): string {
  return text.replace(/[\s\u0007\u200b]+/g, " ").trim();
}

export async function readSectionSnapshot(key: string): Promise<{ text: string; ooxml: string }> {
  return Word.run(async (context) => {
    const start = context.document.getBookmarkRangeOrNullObject(`${key}_START`);
    const end = context.document.getBookmarkRangeOrNullObject(`${key}_END`);
    start.load("isNullObject");
    end.load("isNullObject");
    await context.sync();
    if (start.isNullObject || end.isNullObject) throw new Error(`Section bookmarks are missing for ${key}.`);
    const range = start.expandTo(end);
    range.load("text");
    const xml = range.getOoxml();
    await context.sync();
    return { text: range.text, ooxml: xml.value };
  });
}

export async function readSectionSnapshots(
  keys: string[]
): Promise<Map<string, { text: string; ooxml: string }>> {
  if (keys.length === 0) return new Map();
  return Word.run(async (context) => {
    const pairs = keys.map((key) => {
      const start = context.document.getBookmarkRangeOrNullObject(`${key}_START`);
      const end = context.document.getBookmarkRangeOrNullObject(`${key}_END`);
      start.load("isNullObject");
      end.load("isNullObject");
      return { key, start, end };
    });
    await context.sync();

    const rangeQueries = pairs.map(({ key, start, end }) => {
      if (start.isNullObject || end.isNullObject) {
        throw new Error(`Section bookmarks are missing for ${key}.`);
      }
      const range = start.expandTo(end);
      range.load("text");
      const xml = range.getOoxml();
      return { key, range, xml };
    });
    await context.sync();

    const results = new Map<string, { text: string; ooxml: string }>();
    for (const { key, range, xml } of rangeQueries) {
      results.set(key, { text: range.text, ooxml: xml.value });
    }
    return results;
  });
}

/** Hash only the selected document body, excluding volatile package metadata. */
export async function sectionFingerprint(ooxml: string, key?: string): Promise<string> {
  const parsed = new DOMParser().parseFromString(ooxml, "application/xml");
  const body = parsed.getElementsByTagNameNS("http://schemas.openxmlformats.org/wordprocessingml/2006/main", "body")[0];
  if (!body) throw new Error("Word did not return the section content.");
  const wordNs = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
  const relationshipNs = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
  const packageNs = "http://schemas.microsoft.com/office/2006/xmlPackage";
  const parts = Array.from(parsed.getElementsByTagNameNS(packageNs, "part"));
  const imageFingerprints = new Map<string, string>();
  const relationshipPart = parts.find((part) => part.getAttributeNS(packageNs, "name") === "/word/_rels/document.xml.rels");
  if (relationshipPart) {
    for (const rel of Array.from(relationshipPart.getElementsByTagNameNS("http://schemas.openxmlformats.org/package/2006/relationships", "Relationship"))) {
      if (!rel.getAttribute("Type").endsWith("/image") || rel.getAttribute("TargetMode") === "External") continue;
      const path = new URL(rel.getAttribute("Target"), "https://package.invalid/word/document.xml").pathname;
      const part = parts.find((item) => item.getAttributeNS(packageNs, "name") === path);
      const encoded = part?.getElementsByTagNameNS(packageNs, "binaryData")[0]?.textContent;
      if (!encoded) continue;
      const binary = atob(encoded.replace(/\s/g, ""));
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
      const digest = await crypto.subtle.digest("SHA-256", bytes);
      imageFingerprints.set(rel.getAttribute("Id"), "sha256:" + Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join(""));
    }
  }
  const elements = Array.from(body.getElementsByTagName("*"));
  const hasStart = key && elements.some((node) => node.localName === "bookmarkStart" && node.getAttributeNS(wordNs, "name") === `${key}_START`);
  let active = !hasStart;
  const ignored = ["bookmarkStart", "bookmarkEnd", "proofErr", "sectPr"];
  const volatile = ["paraId", "textId", "anchorId", "editId"];
  const canonical = (node: Element): any => {
    if (!node || ignored.includes(node.localName)) return null;
    return [ `${node.namespaceURI || ""}#${node.localName}`,
      Array.from(node.attributes).filter((attr) => attr.namespaceURI !== "http://www.w3.org/2000/xmlns/" &&
        !attr.localName.startsWith("rsid") && !volatile.includes(attr.localName))
        .map((attr) => [`${attr.namespaceURI || ""}#${attr.localName}`,
          attr.namespaceURI === relationshipNs && attr.localName === "embed" ? imageFingerprints.get(attr.value) || attr.value : attr.value])
        .sort((a, b) => a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0),
      node.children.length ? "" : node.textContent || "",
      Array.from(node.children).map(canonical).filter((item) => item !== null) ];
  };
  const child = (node: Element, name: string) => Array.from(node.children).find((item) => item.namespaceURI === wordNs && item.localName === name);
  const records: any[] = [];
  let lastParagraph: Element = null;
  for (const node of elements) {
    if (node.namespaceURI === wordNs && node.localName === "bookmarkStart" && key) {
      const name = node.getAttributeNS(wordNs, "name");
      if (name === `${key}_START`) active = true;
      if (name === `${key}_END`) break;
    }
    if (!active || node.namespaceURI !== wordNs || !["t", "instrText", "delText", "drawing", "tab", "br"].includes(node.localName)) continue;
    const ancestors: Element[] = [];
    for (let parent = node.parentElement; parent; parent = parent.parentElement) ancestors.push(parent);
    const paragraph = ancestors.find((parent) => parent.namespaceURI === wordNs && parent.localName === "p");
    if (paragraph !== lastParagraph) {
      records.push(["paragraph", paragraph ? canonical(child(paragraph, "pPr")) : null]);
      lastParagraph = paragraph;
    }
    const styles = [];
    for (const parent of ancestors) {
      const prop = { r: "rPr", tbl: "tblPr", tr: "trPr", tc: "tcPr" }[parent.localName];
      if (parent.namespaceURI === wordNs && prop) styles.push([parent.localName, canonical(child(parent, prop))]);
      if (parent.namespaceURI === wordNs && ["ins", "del"].includes(parent.localName)) styles.push([parent.localName]);
    }
    records.push([canonical(node), styles]);
  }
  const bytes = new TextEncoder().encode(JSON.stringify(records));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
